import type {TFunction} from 'i18next'
import {useParams} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {Skeleton} from '@/components/ui/skeleton'
import {Tag} from '@/components/ui/tag'
import {DataTable, type Column, type ColumnGroup} from '@/components/data-table'
import {CeldaEstado} from '@/components/ui/celdas-tabla'
import {GroupHeader} from '@/components/help-icon'
import {NoAccessState} from '@/components/no-access-state'
import {ExpedienteChrome} from '@/components/expediente-chrome'
import {ExpedienteNotFound, ExpedienteEmptySnapshot} from '@/components/expediente-empty'
import {useExpedienteEntry, useExpedienteMatrizRiesgos} from '@/hooks/sgsst/expediente/use-expediente'

// Fila congelada del snapshot IPEVR (backend `SnapshotFila`); el contrato la entrega como
// Record<string, unknown>[].
type SnapshotFila = {
  id: string
  proceso: string | null
  zona: string | null
  tarea: string | null
  clasificacion: string
  descripcion: string
  interpretacion: string | null
  aceptabilidad: string | null
  nr: number | null
  num_expuestos: number | null
}

const INTERP_TONE: Record<string, 'green' | 'amber' | 'red'> = {
  I: 'red',
  II: 'red',
  III: 'amber',
  IV: 'green',
}

function columnas(t: TFunction): Column<SnapshotFila>[] {
  return [
    {
      key: 'peligro',
      header: t('expediente.vistas.matrizRiesgos.cols.peligro'),
      cell: (fila) => <CeldaEstado nombre={fila.descripcion} meta={fila.clasificacion} />,
    },
    {
      key: 'proceso',
      header: t('expediente.vistas.matrizRiesgos.cols.proceso'),
      width: 'w-40',
      className: 'text-muted-foreground',
      cell: (fila) => fila.proceso ?? '—',
    },
    {
      key: 'nr',
      header: t('expediente.vistas.matrizRiesgos.cols.nr'),
      width: 'w-20',
      className: 'text-right font-mono tabular-nums',
      cell: (fila) => fila.nr ?? '—',
    },
    {
      key: 'interpretacion',
      header: t('expediente.vistas.matrizRiesgos.cols.interpretacion'),
      width: 'w-28',
      cell: (fila) =>
        fila.interpretacion ? (
          <Tag tone={INTERP_TONE[fila.interpretacion] ?? 'neutral'}>{fila.interpretacion}</Tag>
        ) : (
          '—'
        ),
    },
    {
      key: 'expuestos',
      header: t('expediente.vistas.matrizRiesgos.cols.expuestos'),
      width: 'w-24',
      className: 'text-right font-mono tabular-nums',
      cell: (fila) => fila.num_expuestos ?? '—',
    },
  ]
}

/** NR e Interpretación son la misma pregunta de la GTC 45: el criterio se dice una vez sobre las dos (U20). */
function GRUPOS(t: TFunction): ColumnGroup[] {
  return [
    {
      startKey: 'nr',
      span: 2,
      content: (
        <GroupHeader
          label={t('expediente.vistas.matrizRiesgos.grupos.valoracion')}
          help={t('expediente.vistas.matrizRiesgos.grupos.valoracionAyuda')}
          testId="expediente-riesgos-valoracion-help"
        />
      ),
    },
  ]
}

/** Filas congeladas del snapshot del cierre, sin acciones de edición: el editor vivo es del presente (UX O3). */
export function ExpedienteMatrizRiesgosPage() {
  const {t} = useTranslation()
  const {businessId, year: yearParam} = useParams()
  const year = Number(yearParam)
  const {canOperate, isLoading, exp} = useExpedienteEntry(businessId, year)
  const {data} = useExpedienteMatrizRiesgos(businessId, year, canOperate && !!exp)

  if (!canOperate) return <NoAccessState />
  if (isLoading) return <Skeleton className="h-40 w-full" />
  if (!exp) return <ExpedienteNotFound businessId={businessId!} year={year} />

  const base = `/businesses/${businessId}`
  const filas = (data?.filas ?? []) as unknown as SnapshotFila[]

  return (
    <ExpedienteChrome exp={exp} backTo={`${base}/expediente/${year}`} backLabel={t('expediente.backToIndex', {year})}>
      <div className="space-y-4" data-testid="expediente-matriz-riesgos">
        {!data?.disponible ? (
          <>
            <h2 className="font-mono text-[11px] font-semibold tracking-wider text-muted-foreground uppercase">
              {t('expediente.sections.matrizRiesgos')}
            </h2>
            <ExpedienteEmptySnapshot message={t('expediente.snapshotAusente.matriz')} />
          </>
        ) : filas.length === 0 ? (
          <>
            <h2 className="font-mono text-[11px] font-semibold tracking-wider text-muted-foreground uppercase">
              {t('expediente.sections.matrizRiesgos')}
            </h2>
            <p className="surface px-6 py-10 text-center text-sm text-muted-foreground">
              {t('expediente.vistas.matrizRiesgos.sinFilas')}
            </p>
          </>
        ) : (
          <section className="surface overflow-hidden">
            <div className="surface-divider border-b px-4 py-3">
              <h2 className="font-heading text-base text-foreground">{t('expediente.sections.matrizRiesgos')}</h2>
            </div>
            <div className="px-4 py-2">
              <DataTable
                columns={columnas(t)}
                columnGroups={GRUPOS(t)}
                rows={filas}
                rowKey={(fila) => fila.id}
                rowTestId={() => 'expediente-riesgo-fila'}
              />
            </div>
          </section>
        )}
      </div>
    </ExpedienteChrome>
  )
}
